import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type DailyForecastSelection,
  WeatherSource,
} from '@ebalance/proto/bffuser/v1/weather_forecast_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { useAreaWeatherForecast } from '~/hooks/weather/useAreaWeatherForecast'
import { toProtoDate } from '~/models/dateTime'
import {
  buildKomaTemperatureForecastValues,
  buildKomaTemperatureLabels,
  buildKomaWeatherIcons,
} from '~/models/weather/koma'

export type UseAreaWeatherIconsParams = {
  areaCode: AreaCode
  /**
   * 対象日 (単日)。気象 gRPC へは `from = to = displayDate` で渡す。応答は
   * 対象日 00:00 〜 翌日 00:00 の手前までの点だけで、翌日 0 時の点は含まれない。
   */
  displayDate: Date
  enabled?: boolean
}

/** targetDate と同じ年月日 (google.type.Date) を指す DailyForecastSelection を探す。 */
function findSelection(
  selections: DailyForecastSelection[],
  targetDate: ProtoDate,
): DailyForecastSelection | undefined {
  return selections.find(
    (s) =>
      s.targetDate !== undefined &&
      s.targetDate.year === targetDate.year &&
      s.targetDate.month === targetDate.month &&
      s.targetDate.day === targetDate.day,
  )
}

/**
 * エリア + 対象日から天気アイコン帯 (30 分刻み 48 個の絵文字列、doji-bg §48 コマ
 * 準拠) と予想気温ラベル (同 48 スロット、正時のみ整数 ℃・他 30 分位置は空)、
 * および気象チャートの予想気温折れ線用の数値系列 (同 48 スロット、発表値のまま)
 * を返す。時系列予報 (VPFD) の天気・気温は正時の点のコマにだけ値を持ち、点以外の
 * コマは空文字 / null (AC-WD-003a)。有効な気温点が 1 つも無い場合は全 null 配列。
 * データ源は気象 gRPC (WeatherForecastService) のライブ予報。展開ロジックは
 * `src/models/weather/koma` の純関数 (weather-icon-unify-48koma) に委譲する。
 *
 * 対象日の `daily_selection` (spec AC-WD-009/010 優先順位で backend が解決済み)
 * を lookup し、`weather_source` で表示元 series を切り替える
 * (jma-select-logic-spec-compliance 設計書 AC-13/14/15):
 * VPFD → `weather3hour` の正時の点のコマにだけ置く / PREFECTURAL・WEEKLY →
 * 日別 1 天気には正時の点が無いので予報アイコンを置かない (doji-bg AC-BDB-002 /
 * AC-BDB-010b。日別 1 値を時間帯別のコマへ複製しない) /
 * UNAVAILABLE・対象日の selection が無い → 空 (気象帯 hidden 相当)。
 * `weatherTemps` / `weatherTempValues` は selection と無関係に常に
 * `forecast.temperature` (VPFD 気温シリーズ) から生成する — 府県 / 週間選択日は
 * 該当日に VPFD 気温行が無いため自然に空になる (設計書「判定入力」の (c))。
 *
 * 取得失敗・地点未対応 (is_available=false) は空配列に降格し、チャート本体の
 * 表示は妨げない (doji-bg AC-372 相当)。
 */
export function useAreaWeatherIcons(params: UseAreaWeatherIconsParams): {
  weatherIcons: string[]
  weatherTemps: string[]
  weatherTempValues: Array<number | null>
} {
  const targetDate = toProtoDate(params.displayDate)
  const { data } = useAreaWeatherForecast({
    areaCode: params.areaCode,
    targetDateFrom: targetDate,
    targetDateTo: targetDate,
    enabled: params.enabled,
  })

  return useMemo(() => {
    const forecast = data?.forecast
    if (!forecast?.isAvailable) {
      return { weatherIcons: [], weatherTemps: [], weatherTempValues: [] }
    }
    const weatherTemps = buildKomaTemperatureLabels(forecast.temperature)
    const weatherTempValues = buildKomaTemperatureForecastValues(
      forecast.temperature,
    )
    const selection = findSelection(forecast.dailySelection, targetDate)
    switch (selection?.weatherSource) {
      case WeatherSource.VPFD:
        return {
          weatherIcons: buildKomaWeatherIcons(forecast.weather3hour),
          weatherTemps,
          weatherTempValues,
        }
      case WeatherSource.PREFECTURAL:
      case WeatherSource.WEEKLY:
        return { weatherIcons: [], weatherTemps, weatherTempValues }
      default:
        return { weatherIcons: [], weatherTemps: [], weatherTempValues }
    }
  }, [data, targetDate])
}
